import { z } from 'zod';

// ---------- devices / session ----------
export const DeviceSchema = z.object({ name: z.string(), added: z.string(), lastUsed: z.string().nullable() });
export const MeSchema = z.object({ device: z.string(), devices: z.array(DeviceSchema) });
export type Device = z.infer<typeof DeviceSchema>;
export type Me = z.infer<typeof MeSchema>;

export const RegisterStartSchema = z.object({
  setupCode: z.string().max(64).default(''),
  deviceName: z.string().max(40).default('Device'),
});
export type RegisterStart = z.infer<typeof RegisterStartSchema>;

// ---------- alerts (Web Push) ----------
export const PushSubscriptionSchema = z.object({
  endpoint: z.url(),
  keys: z.object({ p256dh: z.string().min(1), auth: z.string().min(1) }),
});
export const PushSubscribeSchema = z.object({
  subscription: PushSubscriptionSchema,
  label: z.string().max(40).default('Device'),
});
export const PushUnsubscribeSchema = z.object({ endpoint: z.string().max(2000) });
export const AlertSchema = z.object({
  title: z.string().max(200).default('SERB'),
  body: z.string().max(1000).default(''),
  url: z.string().max(500).default('/'),
  tag: z.string().max(100).optional(),
});
export type PushSubscribe = z.infer<typeof PushSubscribeSchema>;
export type Alert = z.infer<typeof AlertSchema>;
export type AlertDevice = { label: string; added: string };
export type PushStatus = { on: boolean; devices: AlertDevice[] };
export type PushResult = { sent: number; removed: number; failed: number };

// ---------- reminders (clock) ----------
export const AlarmInputSchema = z.object({
  title: z.string().trim().min(1, 'Write what the reminder is about.').max(140),
  /** Jordan time "2026-10-20T09:00", or an exact ISO moment */
  at: z.string().min(1, 'Pick a date and time.'),
  note: z.string().max(500).default(''),
  code: z.string().trim().toUpperCase().max(20).default(''),
  kind: z.string().max(30).default('reminder'),
  urgent: z.boolean().default(false),
  by: z.string().max(30).optional(),
});
export type AlarmInput = z.input<typeof AlarmInputSchema>;

export type Alarm = {
  id: string;
  title: string;
  note: string;
  code: string;
  kind: string;
  by: string;
  urgent: boolean;
  /** when it was asked for */
  at: string;
  /** when it will actually arrive (held to working hours) */
  deliverAt: string;
  created: string;
  firedAt?: string;
  late?: boolean;
};

export type ClockState = {
  now: string;
  nextMorning: string;
  lastMorning: string | null;
  pending: Alarm[];
  done: Alarm[];
};

// ---------- approval cards ----------
export const CARD_STATUSES = [
  'pending',
  'approved',
  'edited',
  'rejected',
  'sent',
  'failed',
  'superseded',
  'expired',
] as const;
export type CardStatus = (typeof CARD_STATUSES)[number];

export const CardInputSchema = z.object({
  kind: z.string().min(1).max(40),
  title: z.string().min(1).max(200),
  code: z.string().trim().toUpperCase().max(20).default(''),
  summary: z.string().max(2000).default(''),
  urgent: z.boolean().default(false),
  source: z.string().max(500).default(''),
  payload: z.record(z.string(), z.unknown()).default({}),
  draft: z.record(z.string(), z.unknown()).default({}),
  actor: z.string().max(60).default('brain'),
});
export type CardInput = z.input<typeof CardInputSchema>;

export const CardDecisionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('approve'), note: z.string().max(1000).default(''), device: z.string().default('app') }),
  z.object({ action: z.literal('reject'), note: z.string().max(1000).default(''), device: z.string().default('app') }),
  z.object({
    action: z.literal('edit'),
    final: z.record(z.string(), z.unknown()),
    note: z.string().max(1000).default(''),
    device: z.string().default('app'),
  }),
]);
export type CardDecision = z.input<typeof CardDecisionSchema>;

/** Ibraheem tells Claude what to change on a card, or to do something differently from now on */
export const CardInstructionSchema = z.object({
  text: z.string().trim().min(1, 'Write what Claude should change first.').max(2000),
  /** true = a lasting rule (SERB Mind → Learned Lessons), false = this time only */
  rule: z.boolean().default(false),
  /** true = he is asking something: Claude answers on the card and changes nothing */
  question: z.boolean().default(false),
  device: z.string().default('app'),
});
export type CardInstruction = z.input<typeof CardInstructionSchema>;

/** The server marks a card as sent or failed, or replaces or retires it, once the work after the decision is done */
export const CardStatusUpdateSchema = z.object({
  status: z.enum(['sent', 'failed', 'superseded', 'expired']),
  note: z.string().max(1000).default(''),
});

/** What Ibraheem wrote to Claude on a card (an instruction, a lasting rule, or a question) and what Claude answered */
export type Instruction = {
  id: number;
  text: string;
  rule: boolean;
  question: boolean;
  at: string;
  answered: boolean;
  /** Claude's reply, in plain words; empty until it has answered */
  answer: string;
  answeredAt: string | null;
};

export type Card = {
  id: string;
  kind: string;
  code: string;
  title: string;
  summary: string;
  status: CardStatus;
  urgent: boolean;
  source: string;
  payload: Record<string, unknown>;
  draft: Record<string, unknown>;
  final: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
  sentAt: string | null;
};

/** A file attached to an email card; `size` is null when the file is no longer in the vault */
export type CardFile = { path: string; name: string; size: number | null };

/** A file attached to an email Ibraheem received, that he can put on an email he is sending */
export type MailAttachment = {
  mailbox: string;
  uid: number;
  /** the part of the email the file is in */
  part: string;
  name: string;
  size: number;
  subject: string;
  from: string;
  date: string | null;
};

export const MailAttachmentPickSchema = z.object({
  mailbox: z.string().min(1).max(200),
  uid: z.number().int().positive(),
  part: z.string().regex(/^[0-9.]+$/, 'Pick a file from the list.'),
  name: z.string().min(1).max(300),
});

/** A card as the app shows it: with the instructions Ibraheem gave Claude on it, and the files that go with it */
export type CardView = Card & {
  instructions: Instruction[];
  files?: CardFile[];
  /** an email card that is also a draft in his Drafts folder (Spark) right now */
  inDrafts?: boolean;
};

// ---------- tasks for Claude ----------
export const TASK_STATUSES = ['waiting', 'working', 'needs_you', 'done', 'failed'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Ibraheem gives Claude a job outside the approvals */
export const TaskInputSchema = z.object({
  task: z.string().trim().min(1, 'Write what you want Claude to do first.').max(6000),
  /** the shipment it is about, if any */
  code: z.string().trim().toUpperCase().max(20).default(''),
  /** Jordan time "2026-10-20T09:00", or an exact ISO moment; empty = as soon as Claude next runs */
  at: z.string().default(''),
  /** true = also keep it as a lasting rule (SERB Mind → Learned Lessons) */
  rule: z.boolean().default(false),
});
export type TaskInput = z.input<typeof TaskInputSchema>;

/** Claude reports how a task is going */
export const TaskUpdateSchema = z.object({
  status: z.enum(TASK_STATUSES),
  result: z.string().max(2000).default(''),
  /** the approval card that needs Ibraheem, for "needs_you" */
  cardId: z.string().max(60).optional(),
});

export type Task = {
  id: string;
  task: string;
  code: string;
  /** when Claude should do it (ISO); null = as soon as it next runs */
  runAt: string | null;
  rule: boolean;
  status: TaskStatus;
  result: string;
  cardId: string | null;
  createdAt: string;
  updatedAt: string;
};

// ---------- the brain: Claude running on the server ----------
export const BRAIN_STATUSES = ['running', 'ok', 'failed'] as const;
export type BrainStatus = (typeof BRAIN_STATUSES)[number];

/** One time the server started Claude, and why */
export type BrainRun = {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: BrainStatus;
  /** what woke it: "new mail SRB-551", "task", "morning check", … */
  reasons: string[];
  /** what Claude said it did, or why the run failed */
  summary: string;
  costUsd: number | null;
};

// ---------- live channel (server-sent events) ----------
export type LiveEvents = {
  hello: { serverTime: string };
  tick: { serverTime: string };
  clock: ClockState;
  card: Card;
  task: Task;
  brain: BrainRun;
};
export type LiveEventName = keyof LiveEvents;

/** Error body every API failure returns */
export type ApiErrorBody = { error: string };

// ---------- email watchman (read-only for now) ----------
/** One email the watchman looked at (emails_seen), as the brain and the mail-check command read it */
export type WatchedMail = {
  messageId: string;
  mailbox: string;
  uid: number | null;
  from: string;
  subject: string;
  receivedAt: string | null;
  /** SRB codes found, comma separated ("" when none) */
  code: string;
  /** new = woke the brain and waits for it; ignored = no code, not an agent */
  status: 'new' | 'handled' | 'ignored' | 'failed';
  seenAt: string;
};
/** How each watched folder is doing */
export type WatchmanStatus = {
  on: boolean;
  /** why it is off, in plain words (no mail login on this machine, switched off) */
  note?: string;
  mailboxes: {
    mailbox: string;
    connected: boolean;
    /** last message number handled in this folder */
    lastUid: number | null;
    lastMailAt: string | null;
    lastError: string | null;
  }[];
};

/** Which release of the app is running (from /health) */
export type AppVersion = { commit: string; startedAt: string };
